"use client"

import { createContext, useContext, useState, useEffect, useRef, type ReactNode } from "react"
import type { Task, TimesheetEntry } from "@/lib/types"
import { createTimesheetEntry, submitTimesheetsToWP, SubmitTimesheetsResponse } from "@/lib/notion-client"
import { useToast } from "@/hooks/use-toast"

interface TimerContextType {
  isRunning: boolean
  startTime: Date | null
  elapsedSeconds: number
  currentEntry: TimesheetEntry | null
  selectedTask: Task | null
  startTimer: (task: Task) => Promise<void>
  pauseTimer: () => void
  resumeTimer: () => void
  stopTimer: () => TimesheetEntry | null
  submitAllData: (timesheets: TimesheetEntry[], taskStatusUpdates?: Array<{ taskID: string; status: string }>) => Promise<SubmitTimesheetsResponse | void>
}

const TimerContext = createContext<TimerContextType | undefined>(undefined)

export function TimerProvider({ children }: { children: ReactNode }) {
  const { toast } = useToast()
  const [isRunning, setIsRunning] = useState(false)
  const [startTime, setStartTime] = useState<Date | null>(null)
  const [elapsedSeconds, setElapsedSeconds] = useState(0)
  const [accumulatedSeconds, setAccumulatedSeconds] = useState(0)
  const [currentEntry, setCurrentEntry] = useState<TimesheetEntry | null>(null)
  const [selectedTask, setSelectedTask] = useState<Task | null>(null)
  const intervalRef = useRef<NodeJS.Timeout>()

  useEffect(() => {
    if (isRunning && startTime) {
      intervalRef.current = setInterval(() => {
        const currentElapsed = Math.floor((new Date().getTime() - startTime.getTime()) / 1000)
        setElapsedSeconds(accumulatedSeconds + currentElapsed)
      }, 1000)
    } else {
      if (intervalRef.current) {
        clearInterval(intervalRef.current)
      }
    }

    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current)
      }
    }
  }, [isRunning, startTime, accumulatedSeconds])

  const startTimer = async (task: Task) => {
    const now = new Date()
    setStartTime(now)
    setIsRunning(true)
    setSelectedTask(task)
    setAccumulatedSeconds(0)

    const entry = await createTimesheetEntry({
      taskId: task.id,
      userId: "user-1",
      status: "Draft",
      startTime: now,
      endTime: null,
      manualMinutes: 0,
      isSubmitted: false,
    })

    // Convert NotionTimesheet to TimesheetEntry for currentEntry state
    const timesheetEntry: TimesheetEntry = {
      id: entry.id,
      taskId: entry.taskId,
      userId: entry.userId,
      status: entry.status,
      startTime: entry.startTime.toISOString(),
      endTime: entry.endTime ? entry.endTime.toISOString() : undefined,
      manualMinutes: entry.manualMinutes,
    }
    setCurrentEntry(timesheetEntry)
  // Timer started for task
  }

  const pauseTimer = () => {
    if (startTime) {
      const currentElapsed = Math.floor((new Date().getTime() - startTime.getTime()) / 1000)
      setAccumulatedSeconds(accumulatedSeconds + currentElapsed)
    }
    setIsRunning(false)
  // Timer paused
  }

  const resumeTimer = () => {
    setStartTime(new Date())
    setIsRunning(true)
  // Timer resumed
  }

  const stopTimer = () => {
    if (!currentEntry || !startTime) return null

    const endTime = new Date()
    setIsRunning(false)

    const completedEntry: TimesheetEntry = {
      ...currentEntry,
      endTime: endTime.toISOString(),
    }

    setCurrentEntry(null)
    setStartTime(null)
    setElapsedSeconds(0)
    setAccumulatedSeconds(0)
    setSelectedTask(null)

  // Timer stopped, entry completed
    return completedEntry
  }

  const submitAllData = async (timesheets: TimesheetEntry[], taskStatusUpdates?: Array<{ taskID: string; status: string }>) => {
    // Build payload and forward to server
    const payload = timesheets.map((t) => ({
      id: t.id,
      taskId: t.taskId,
      userId: t.userId,
      startTime: t.startTime || null,
      endTime: t.endTime || null,
      manualMinutes: t.manualMinutes,
      status: t.status,
    }))

    try {
      const resp = await submitTimesheetsToWP(payload, taskStatusUpdates)

      // Only mark entries as submitted locally if server returned ok
      if (resp && resp.status === 'ok') {
        timesheets.forEach((entry) => {
          if (entry) {
            entry.status = "Submitted";
            entry.isSubmitted = true;
          }
        });
        setCurrentEntry(null)
        setStartTime(null)
        setElapsedSeconds(0)
        setAccumulatedSeconds(0)
        setSelectedTask(null)
      }

      return resp
    } catch (err: any) {
      // Surface error to caller so UI can show dialog; also show toast
      toast({ title: 'Submission failed', description: String(err?.message ?? String(err)) })
      throw err
    }
  }

  return (
    <TimerContext.Provider
      value={{
        isRunning,
        startTime,
        elapsedSeconds,
        currentEntry,
        selectedTask,
        startTimer,
        pauseTimer,
        resumeTimer,
        stopTimer,
        submitAllData,
      }}
    >
      {children}
    </TimerContext.Provider>
  )
}

export function useTimer() {
  const context = useContext(TimerContext)
  if (context === undefined) {
    throw new Error("useTimer must be used within a TimerProvider")
  }
  return context
}
